import { useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Feature } from "geojson";
import { InputSearchFilterControl } from "components/Moleculas/Map/InputSearchFilter";
import { useTypedSelector } from "store/hooks";
import { PropriedadesFeicao } from "store/modules/consultaprevia/types";
import { ItemResult, ResultadoBusca } from "./ItemResult";

type Filtros = {
  search?: string;
};

const normalizar = (valor: string) =>
  valor
    .normalize("NFD")
    // Busca por "area" tem que achar "Área": o cidadão não digita acento.
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

const paraResultados = (
  features: Feature[],
  origem: ResultadoBusca["origem"]
): ResultadoBusca[] =>
  features.map((feature) => {
    const props = (feature.properties ?? {}) as Partial<PropriedadesFeicao>;

    return {
      id: `${origem}-${props.id ?? props.sigla}`,
      sigla: props.sigla ?? "",
      nome: props.nome ?? "",
      tipo: props.tipo ?? "",
      origem,
      feature
    };
  });

/**
 * Busca local sobre as coleções já carregadas. A API da consulta prévia não
 * expõe busca por nome, e não precisa: as duas coleções inteiras já estão em
 * memória para desenhar o mapa — consultar o servidor de novo só adicionaria
 * latência.
 */
export const InputSearchCamadas = () => {
  const methods = useForm<Filtros>();
  const [termo, setTermo] = useState("");

  const zonas = useTypedSelector(
    (state) => state.consultaprevia.obterZonasGeo.successPayload
  );
  const areas = useTypedSelector(
    (state) => state.consultaprevia.obterAreasEspeciaisGeo.successPayload
  );

  const todas = useMemo(
    () => [
      ...paraResultados(zonas?.features ?? [], "zona"),
      ...paraResultados(areas?.features ?? [], "area")
    ],
    [zonas, areas]
  );

  const resultados = useMemo(() => {
    const busca = normalizar(termo);

    if (!busca) return [];

    return todas.filter(
      (item) =>
        normalizar(item.sigla).includes(busca) ||
        normalizar(item.nome).includes(busca)
    );
  }, [todas, termo]);

  return (
    <FormProvider {...methods}>
      <InputSearchFilterControl
        inputSearchPlaceholder="Pesquisar"
        onSubmit={(values) => setTermo(values.search ?? "")}
        onClean={() => setTermo("")}
        isLoadingResult={false}
        itemsResult={resultados}
        render={ItemResult}
      />
    </FormProvider>
  );
};
